/**
 * Tooltip-specific markdown renderer.
 *
 * Unlike LazyMarkdown (which uses Suspense with an empty fallback), this
 * component renders the raw text as a plain <span> while react-markdown
 * loads, preventing the layout shift that causes tooltip flickering.
 *
 * After the first successful load, the resolved component is cached at
 * module level — every subsequent render is fully synchronous.
 */
import { useEffect, useReducer, type ComponentType } from "react";
import type { Options } from "react-markdown";

/** Module-level cache — survives across mounts, zero async after first load. */
let Resolved: ComponentType<Options> | null = null;
let loadPromise: Promise<void> | null = null;

function loadMarkdown(): Promise<void> {
  if (!loadPromise) {
    loadPromise = Promise.all([
      import("react-markdown"),
      import("remark-gfm"),
    ]).then(([md, gfm]) => {
      Resolved = (props: Options) => {
        const Md = md.default;
        return <Md remarkPlugins={[gfm.default, ...(props.remarkPlugins || [])]} {...props} />;
      };
    });
  }
  return loadPromise;
}

export default function TooltipMarkdown(props: Options) {
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

  useEffect(() => {
    if (!Resolved) {
      loadMarkdown().then(forceUpdate);
    }
  }, []);

  if (Resolved) {
    return <Resolved {...props} />;
  }

  // Stable fallback — plain text with same max-width so dimensions don't jump
  return <span className={props.className ?? undefined}>{props.children}</span>;
}
